import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, useField } from 'react-final-form-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import { useTheme } from '../../ThemeContext';

import {
  welcomeSetPasswordInit,
  welcomeSetPassword,
} from '../../actions/Auth/AuthActions';

import IMAGE_LOGO from '../../assets/Images/Logo.svg';
import IMAGE_CHECK from '../../assets/Images/Check.svg';

import {
  Panel,
  Logobox,
  Logo,
  Textbox,
  Text,
  Strong,
  Form,
  Inputbox,
  Input,
  InputError,
  SubmitButton,
  Linkbox,
  Successbox,
  SuccessImage,
} from './WelcomePanel.style';

const WelcomePanel = ({ email, oobCode }) => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const authWelcomeSetPasswordStore = useSelector(
    (state) => state.authWelcomeSetPasswordStore
  );
  const { isLoading, isSuccess, isError, error } = authWelcomeSetPasswordStore;
  const dispatch = useDispatch();

  const onSubmit = (values) => {
    dispatch(
      welcomeSetPassword({
        email: email,
        oobCode: oobCode,
        password: values.password,
      })
    ).then((res) => {
      if (res) history.push('/');
    });
  };

  const validate = (values) => {
    const errors = {};

    if (!values.password)
      errors.password = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.repeatPassword)
      errors.repeatPassword = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (values.password && values.password.length < 6) {
      errors.password = intl.formatMessage({
        id: 'FORM.VALIDATION.THE_PASSWORD_IS_TOO_WEEK',
      });
    }

    if (
      !errors.password &&
      values.repeatPassword &&
      values.password !== values.repeatPassword
    )
      errors.repeatPassword = intl.formatMessage({
        id:
          'FORM.VALIDATION.THE_REPEAT_PASSWORD_SHOULD_BE_THE_SAME_AS_THE_NEW_PASSWORD',
      });

    return errors;
  };

  const { form, handleSubmit, pristine, submitting, invalid } = useForm({
    onSubmit,
    validate,
  });

  const passwordField = useField('password', form);
  const repeatPasswordField = useField('repeatPassword', form);

  useEffect(() => {
    dispatch(welcomeSetPasswordInit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Panel>
      <Logobox>
        <Logo src={IMAGE_LOGO} />
      </Logobox>
      <Textbox>
        {isError ? (
          <Text theme={theme.sign.text.error}>
            {intl.formatMessage({
              id: `WELCOME_PANEL.TEXT.ERROR.${error}`,
              defaultMessage: error,
            })}
          </Text>
        ) : isSuccess ? (
          <>
            <Text theme={theme.sign.text.default}>
              {intl.formatMessage({
                id: `WELCOME_PANEL.SUCCESS`,
              })}
            </Text>
            <Successbox>
              <SuccessImage
                src={IMAGE_CHECK}
                alt={intl.formatMessage({
                  id: `WELCOME_PANEL.SUCCESS`,
                })}
              />
            </Successbox>
          </>
        ) : (
          <Text theme={theme.sign.text.default}>
            <Strong>
              {intl.formatMessage({
                id: 'WELCOME_PANEL.INFO.SET_YOUR_NEW_PASSWORD',
              })}
            </Strong>

            <Text theme={theme.sign.text.default}>{email}</Text>
          </Text>
        )}
      </Textbox>

      {!isSuccess ? (
        <Form onSubmit={handleSubmit}>
          <Inputbox>
            <Input
              theme={theme.input.default}
              {...passwordField.input}
              type='password'
              placeholder={intl.formatMessage({
                id: 'WELCOME_PANEL.INPUT.NEW_PASSWORD',
              })}
              autoComplete='on'
              invalid={passwordField.meta.touched && passwordField.meta.error}
              valid={
                passwordField.input.value &&
                !(passwordField.meta.touched && passwordField.meta.error)
              }
            />
            <InputError theme={theme.inputError.default}>
              {passwordField.meta.touched && passwordField.meta.error}
            </InputError>
          </Inputbox>

          <Inputbox>
            <Input
              theme={theme.input.default}
              {...repeatPasswordField.input}
              type='password'
              placeholder={intl.formatMessage({
                id: 'WELCOME_PANEL.INPUT.REPEAT_PASSWORD',
              })}
              autoComplete='on'
              invalid={
                repeatPasswordField.meta.touched &&
                repeatPasswordField.meta.error
              }
              valid={
                repeatPasswordField.input.value &&
                !(
                  repeatPasswordField.meta.touched &&
                  repeatPasswordField.meta.error
                )
              }
            />
            <InputError theme={theme.inputError.default}>
              {repeatPasswordField.meta.touched &&
                repeatPasswordField.meta.error}
            </InputError>
          </Inputbox>

          <SubmitButton
            theme={theme.button.default}
            disabled={submitting || pristine || invalid || isLoading}
          >
            {isLoading
              ? intl.formatMessage({
                  id: 'WELCOME_PANEL.SUBMIT_BUTTON.PLEASE_WAIT',
                })
              : intl.formatMessage({
                  id: 'WELCOME_PANEL.SUBMIT_BUTTON.CHANGE_PASSWORD',
                })}
          </SubmitButton>

          <Linkbox theme={theme.sign.linkbox}>
            <Link to={`/`}>
              {intl.formatMessage({
                id: 'WELCOME_PANEL.BUTTON.BACK_TO_SIGN_IN',
              })}
              .
            </Link>
          </Linkbox>
        </Form>
      ) : (
        <Linkbox theme={theme.sign.linkbox}>
          <Link to={`/`}>
            {intl.formatMessage({
              id: 'WELCOME_PANEL.BUTTON.BACK_TO_SIGN_IN',
            })}
            .
          </Link>
        </Linkbox>
      )}
    </Panel>
  );
};

WelcomePanel.propTypes = {
  email: PropTypes.string,
  oobCode: PropTypes.string,
};

export default WelcomePanel;
